import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { TabView } from 'react-native-tab-view';
import KPICard from '@/components/Charts/KPICard';
import Avatar from '@/components/People/Avatar';
import ReachTabBar from '@/components/General/ReachTabBar';
import { Query } from '@/utils/react-query/Queries';
import usePlatform from '@/utils/hooks/usePlatform';
import Callout from '@/components/General/Callout';
import CutCard from '@/components/General/CutCard';
import { useRouter, useLocalSearchParams, useNavigation, usePathname } from 'expo-router';
import { ActivityIndicator } from 'react-native-paper';
import AnalyticsDateRangeControls from '@/components/Charts/AnalyticsDateRangeControls';
import Checkbox from '@/components/General/inputs/Checkbox';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import { DateTime } from 'luxon';
import { dayContainer } from '@/utils/reportDates';
import Chart from '@/components/Charts/Chart';
import SaveShortcutModal from '@/components/General/SaveShortcutModal';
import DropdownMenu from '@/components/General/DropdownMenu';
import PageTitle from '@/components/General/PageTitle';
import { queryClient } from '@/data/client';

const DATE_PRESETS = [
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'Year to Date', value: 'year_to_date' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Last Year', value: 'last_year' },
    { label: 'Past 30 Days', value: 'last_30_days' },
    { label: 'Past 90 Days', value: 'last_90_days' },
    { label: 'Past 12 Months', value: 'last_12_months' },
];

// Dates are day containers: device-local midnight of the org-local calendar day.
const getPresetDates = (preset, timeZone) => {
    const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
    const day = (dt) => dayContainer(dt);
    switch (preset) {
        case 'this_week':
            return { start: day(now.startOf('week')), end: day(now.endOf('week')) };
        case 'last_week':
            return { start: day(now.minus({ weeks: 1 }).startOf('week')), end: day(now.minus({ weeks: 1 }).endOf('week')) };
        case 'this_month':
            return { start: day(now.startOf('month')), end: day(now.endOf('month')) };
        case 'last_month':
            return { start: day(now.minus({ months: 1 }).startOf('month')), end: day(now.minus({ months: 1 }).endOf('month')) };
        case 'this_quarter':
            return { start: day(now.startOf('quarter')), end: day(now.endOf('quarter')) };
        case 'last_quarter':
            return { start: day(now.minus({ quarters: 1 }).startOf('quarter')), end: day(now.minus({ quarters: 1 }).endOf('quarter')) };
        case 'this_year':
            return { start: day(now.startOf('year')), end: day(now.endOf('year')) };
        case 'last_year':
            return { start: day(now.minus({ years: 1 }).startOf('year')), end: day(now.minus({ years: 1 }).endOf('year')) };
        case 'last_30_days':
            return { start: day(now.minus({ days: 30 })), end: day(now) };
        case 'last_90_days':
            return { start: day(now.minus({ days: 90 })), end: day(now) };
        case 'year_to_date':
            return { start: day(now.startOf('year')), end: day(now) };
        case 'last_12_months':
            return { start: day(now.minus({ months: 12 })), end: day(now) };
        default:
            return null;
    }
};

const SectionHeader = ({ title, subtitle }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionAccent} />
        <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {subtitle ? <Text style={styles.sectionSubtitle}>{subtitle}</Text> : null}
        </View>
    </View>
);

const GroupLabel = ({ children }) => (
    <Text style={styles.groupLabel}>{children}</Text>
);

const getLeaderName = (row) => row?.leader_full_name || row?.leader_name
    || `${row?.leader_first_name || row?.leader?.first_name || row?.leader?.firstName || ''} ${row?.leader_last_name || row?.leader?.last_name || row?.leader?.lastName || ''}`.trim()
    || 'Unassigned leader';

const numberValue = (value) => Number(value) || 0;
const getSessionDate = (row) => row?.session_date || row?.date;

const getAttendeeName = (attendee) => attendee?.name
    || attendee?.fullName
    || attendee?.full_name
    || `${attendee?.first_name || attendee?.firstName || ''} ${attendee?.last_name || attendee?.lastName || ''}`.trim()
    || 'Unknown attendee';

const getAttendeeClassifications = (attendee) => {
    const attendanceType = String(attendee?.memberGuestClassification || attendee?.classification || attendee?.attendee_type || attendee?.type || '').toLowerCase();
    const ageType = String(attendee?.ageClassification || attendee?.age_classification || attendee?.age_group || attendee?.age_category || '').toLowerCase();
    const isGuest = attendee?.is_guest === true || attendee?.isGuest === true || attendanceType === 'guest';
    const isMember = attendee?.is_group_member === true || attendee?.isGroupMember === true || attendee?.is_member === true || attendanceType === 'member';
    const age = attendee?.is_child === true || ageType === 'child'
        ? 'Child'
        : attendee?.is_adult === true || ageType === 'adult'
            ? 'Adult'
            : 'No age';

    return [isGuest ? 'Guest' : isMember ? 'Member' : 'Attendee', age];
};

const getMetadataEntries = (metadata) => {
    if (Array.isArray(metadata)) return metadata;
    if (!metadata || typeof metadata !== 'object') return [];
    return Object.entries(metadata).map(([id, value]) => (
        value && typeof value === 'object' ? { id, ...value } : { id, name: id }
    ));
};

const formatCustomFieldValue = (value, field = {}) => {
    if (value === null || value === undefined || value === '') return null;
    const type = String(field.type || field.field_type || '').toLowerCase();
    if (type === 'checkbox' || typeof value === 'boolean') {
        return value === true || value === 'true' || value === 1 ? 'Yes' : 'No';
    }

    const formatSingleValue = (item) => {
        const options = Array.isArray(field.options) ? field.options : [];
        const matchedOption = options.find(option => (
            typeof option === 'object' ? option.value === item || option.id === item : option === item
        ));
        if (matchedOption && typeof matchedOption === 'object') return matchedOption.label || matchedOption.name || String(item);
        if (type.includes('date') && typeof item === 'string') {
            const date = DateTime.fromISO(item);
            if (date.isValid) return date.toLocaleString(type === 'datetime' ? DateTime.DATETIME_MED : DateTime.DATE_MED);
        }
        if (item && typeof item === 'object') {
            return item.name || item.fileName || item.filename || item.label || item.originalName || item.value || null;
        }
        return String(item);
    };

    const values = Array.isArray(value) ? value : [value];
    return values.map(formatSingleValue).filter(Boolean).join(', ') || null;
};

const Stat = ({ label, value }) => (
    <View style={styles.leaderStat}>
        <Text style={styles.leaderStatValue}>{value}</Text>
        <Text style={styles.leaderStatLabel}>{label}</Text>
    </View>
);

const LeaderStat = ({ label, value }) => (
    <View style={styles.leaderCompactStat}>
        <Text style={styles.leaderCompactStatValue}>{value}</Text>
        <Text style={styles.leaderCompactStatLabel}>{label}</Text>
    </View>
);

const MeetingDetailSheet = ({ meeting, onClose }) => {
    const attendees = Array.isArray(meeting?.attendees) ? meeting.attendees : [];
    const metadata = getMetadataEntries(meeting?.custom_field_metadata);
    const values = meeting?.custom_field_values && typeof meeting.custom_field_values === 'object'
        ? meeting.custom_field_values
        : {};
    const customFields = metadata.map(field => {
        const id = field.id || field.field_id || field.custom_field_id;
        return {
            id,
            label: field.name || field.label || 'Custom field',
            value: formatCustomFieldValue(values[id], field),
        };
    }).filter(field => field.value !== null);

    const sessionDate = getSessionDate(meeting);
    const title = sessionDate
        ? DateTime.fromISO(sessionDate).toFormat('MMM d, yyyy')
        : 'Did not meet';

    return (
        <SheetModal
            isOpen={!!meeting}
            onCancel={onClose}
            onClose={onClose}
            title={meeting?.small_group_name || 'Meeting details'}
            subtitle={title}
            width={620}
        >
            <ScrollView
                style={styles.meetingDetailScroll}
                contentContainerStyle={styles.meetingDetailContent}
                testID="leader-meeting-detail-modal"
            >
                <View style={styles.detailCountGrid}>
                    <Stat label="Attendees" value={numberValue(meeting?.total_attendee_count)} />
                    <Stat label="Adults" value={numberValue(meeting?.adult_count)} />
                    <Stat label="Children" value={numberValue(meeting?.child_count)} />
                    <Stat label="No age" value={numberValue(meeting?.unknown_age_count)} />
                    <Stat label="Guests" value={numberValue(meeting?.guest_count)} />
                </View>

                <View style={styles.detailSection}>
                    <View style={styles.customFieldRow}>
                        <Text style={styles.customFieldLabel}>Notes</Text>
                        <Text style={meeting?.session_notes ? styles.customFieldValue : styles.detailEmptyText}>
                            {meeting?.session_notes || 'No notes recorded.'}
                        </Text>
                    </View>
                    {customFields.map(field => (
                        <View key={field.id || field.label} style={styles.customFieldRow}>
                            <Text style={styles.customFieldLabel}>{field.label}</Text>
                            <Text style={styles.customFieldValue}>{field.value}</Text>
                        </View>
                    ))}
                </View>

                <View style={styles.detailSection}>
                    <Text style={styles.detailSectionTitle}>Attendance</Text>
                    {attendees.length ? attendees.map((attendee, index) => {
                        const attendeeID = attendee?.person_id || attendee?.personID || attendee?.id;
                        const classifications = getAttendeeClassifications(attendee);
                        return (
                            <View key={attendeeID || `${getAttendeeName(attendee)}-${index}`} style={styles.attendeeRow}>
                                <Avatar
                                    profile={{
                                        id: attendeeID,
                                        firstName: attendee?.first_name || attendee?.firstName,
                                        lastName: attendee?.last_name || attendee?.lastName,
                                        avatarStorageKey: attendee?.avatar_storage_key || attendee?.avatar?.storage_key || attendee?.avatarStorageKey,
                                        avatarUpdatedAt: attendee?.avatar_updated_at || attendee?.avatar?.updated_at || attendee?.avatarUpdatedAt,
                                        uri: attendee?.avatar?.uri || (typeof attendee?.avatar === 'string' && attendee.avatar.startsWith('http') ? attendee.avatar : null),
                                    }}
                                    size={36}
                                    disableBorder
                                />
                                <View style={styles.attendeeInfo}>
                                    <Text style={styles.attendeeName}>{getAttendeeName(attendee)}</Text>
                                    <Text style={styles.attendeeClassification}>{classifications.join(' · ')}</Text>
                                </View>
                            </View>
                        );
                    }) : <Text style={styles.detailEmptyText}>No attendance recorded.</Text>}
                </View>
            </ScrollView>
        </SheetModal>
    );
};

const LeadersTab = React.memo(({
    selectedType,
    startDate,
    endDate,
    selectedPreset,
    handlePresetChange,
    handleStartDateChange,
    handleEndDateChange,
    isNative,
    isAdmin,
}) => {
    const { width } = useWindowDimensions();
    const isNarrow = width < 600;
    const [includeWithoutMeetings, setIncludeWithoutMeetings] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState(null);
    const {
        data: report = [],
        isLoading,
        isError,
        isRefetching,
        refetch,
    } = Query.smallGroups.leaderReport({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
        includeWithoutMeetings: true,
        options: { enabled: !!isAdmin && !!selectedType?.id && !!startDate && !!endDate },
    });

    const leaders = useMemo(() => {
        const visibleRows = includeWithoutMeetings ? report : report.filter(row => !!row.session_id);
        const byLeader = new Map();
        visibleRows.forEach((row) => {
            const leaderID = row.leader_id || row.leader?.id || `unassigned-${row.small_group_id}`;
            if (!byLeader.has(leaderID)) {
                byLeader.set(leaderID, {
                    id: leaderID,
                    name: getLeaderName(row),
                    avatarStorageKey: row.leader_avatar_storage_key || row.leader_avatar?.storage_key || row.leader?.avatar_storage_key || row.leader?.avatarStorageKey,
                    avatarUpdatedAt: row.leader_avatar_updated_at || row.leader_avatar?.updated_at || row.leader?.avatar_updated_at || row.leader?.avatarUpdatedAt,
                    avatarUri: row.leader_avatar?.uri || (typeof row.leader_avatar === 'string' && row.leader_avatar.startsWith('http') ? row.leader_avatar : null),
                    rows: [],
                });
            }
            byLeader.get(leaderID).rows.push(row);
        });

        return [...byLeader.values()].map(leader => ({
            ...leader,
            rows: leader.rows.sort((a, b) => {
                const aDate = getSessionDate(a);
                const bDate = getSessionDate(b);
                if (!aDate) return 1;
                if (!bDate) return -1;
                return DateTime.fromISO(bDate).toMillis() - DateTime.fromISO(aDate).toMillis();
            }),
        })).sort((a, b) => a.name.localeCompare(b.name));
    }, [report, includeWithoutMeetings]);

    if (!isAdmin) return null;

    return (
        <>
            <ScrollView
                contentContainerStyle={styles.screen}
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}
            >
                <SectionHeader title="Leader Sessions" subtitle="Attendance and meeting details by leader" />
                <AnalyticsDateRangeControls
                    presets={DATE_PRESETS}
                    selectedPreset={selectedPreset}
                    startDate={startDate}
                    endDate={endDate}
                    onPresetChange={handlePresetChange}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    isNative={isNative}
                    testIDPrefix="leaders-date-range"
                >
                    <Checkbox
                        checked={includeWithoutMeetings}
                        onChange={setIncludeWithoutMeetings}
                        label="Show leaders who did not meet"
                        helpText="Shows active leader/group assignments without a session in this date range."
                        testID="leaders-include-without-meetings"
                    />
                    <TouchableOpacity
                        style={styles.refreshButton}
                        onPress={refetch}
                        disabled={isRefetching}
                        testID="leaders-refresh"
                        accessibilityRole="button"
                        accessibilityLabel="Refresh leader report"
                    >
                        {isRefetching ? <ActivityIndicator size={16} /> : <Unicon name="refresh" size={18} colorVariant="primary" />}
                        <Text style={styles.refreshButtonText}>Refresh</Text>
                    </TouchableOpacity>
                </AnalyticsDateRangeControls>

                {isLoading ? (
                    <CutCard accentColor="primary" style={styles.reportState} contentStyle={styles.reportStateContent} testID="leaders-loading">
                        <ActivityIndicator size={30} />
                        <Text style={styles.reportStateText}>Loading leader report…</Text>
                    </CutCard>
                ) : isError ? (
                    <CutCard accentColor="primary" style={styles.reportState} contentStyle={styles.reportStateContent} testID="leaders-error">
                        <Text style={styles.reportErrorTitle}>Could not load leader report</Text>
                        <Text style={styles.reportStateText}>Refresh or try again in a moment.</Text>
                        <TouchableOpacity style={styles.retryButton} onPress={refetch} testID="leaders-retry">
                            <Text style={styles.retryButtonText}>Retry</Text>
                        </TouchableOpacity>
                    </CutCard>
                ) : leaders.length === 0 ? (
                    <CutCard accentColor="primary" style={styles.reportState} contentStyle={styles.reportStateContent} testID="leaders-empty">
                        <Text style={styles.reportErrorTitle}>No leader sessions found</Text>
                        <Text style={styles.reportStateText}>
                            {includeWithoutMeetings ? 'No leaders or groups match this range.' : 'Turn on “Include leaders and groups with no meeting” to see non-meeting assignments.'}
                        </Text>
                    </CutCard>
                ) : (
                    <View style={styles.leaderGrid}>
                        {leaders.map(leader => {
                    const meetings = leader.rows.filter(row => !!row.session_id);
                    const totals = meetings.reduce((result, row) => ({
                        attendees: result.attendees + numberValue(row.total_attendee_count),
                        adults: result.adults + numberValue(row.adult_count),
                        children: result.children + numberValue(row.child_count),
                        unknown: result.unknown + numberValue(row.unknown_age_count),
                        guests: result.guests + numberValue(row.guest_count),
                    }), { attendees: 0, adults: 0, children: 0, unknown: 0, guests: 0 });

                    return (
                        <CutCard key={leader.id} accentColor="primary" style={styles.leaderCard(isNarrow)} contentStyle={styles.leaderCardContent} testID={`leader-card-${leader.id}`}>
                            <View style={styles.leaderCardHeader}>
                                <Avatar
                                    profile={{ id: leader.id, avatarStorageKey: leader.avatarStorageKey, avatarUpdatedAt: leader.avatarUpdatedAt, uri: leader.avatarUri }}
                                    size={40}
                                    disableBorder
                                />
                                <View style={styles.leaderHeading}>
                                    <Text style={styles.leaderCardName}>{leader.name}</Text>
                                    <Text style={styles.leaderMeetingCount}>{meetings.length} meeting{meetings.length === 1 ? '' : 's'}</Text>
                                </View>
                                <View style={styles.leaderAttendeeTotal}>
                                    <Text style={styles.leaderAttendeeTotalValue}>{totals.attendees}</Text>
                                    <Text style={styles.leaderAttendeeTotalLabel}>Attendees</Text>
                                </View>
                            </View>
                            <View style={styles.leaderStatsGrid}>
                                <LeaderStat label="Adults" value={totals.adults} />
                                <LeaderStat label="Children" value={totals.children} />
                                <LeaderStat label="No age" value={totals.unknown} />
                                <LeaderStat label="Guests" value={totals.guests} />
                            </View>
                            <ScrollView
                                style={styles.meetingListScroll}
                                contentContainerStyle={styles.meetingList}
                                nestedScrollEnabled
                                showsVerticalScrollIndicator={leader.rows.length > 4}
                            >
                                {leader.rows.map((meeting, index) => {
                                    const didMeet = !!meeting.session_id;
                                    return (
                                        <TouchableOpacity
                                            key={meeting.session_id || `${meeting.small_group_id}-no-meeting-${index}`}
                                            style={styles.meetingRow}
                                            onPress={didMeet ? () => setSelectedMeeting(meeting) : undefined}
                                            disabled={!didMeet}
                                            testID={didMeet ? `leader-meeting-row-${meeting.session_id}` : `leader-no-meeting-row-${meeting.small_group_id}`}
                                            accessibilityRole="button"
                                            accessibilityState={{ disabled: !didMeet }}
                                        >
                                            <View style={styles.meetingMain}>
                                                <Text style={styles.meetingDate(didMeet)}>
                                                    {didMeet && getSessionDate(meeting) ? DateTime.fromISO(getSessionDate(meeting)).toFormat('MMM d, yyyy') : 'Did not meet'}
                                                </Text>
                                                <Text style={styles.meetingGroup}>{meeting.small_group_name || 'Unnamed group'}</Text>
                                            </View>
                                            {didMeet ? (
                                                <View style={styles.meetingArrow}>
                                                    <Unicon name="chevron-forward" size={18} colorVariant="textSecondary" />
                                                </View>
                                            ) : <Text style={styles.didNotMeetLabel}>No session in range</Text>}
                                        </TouchableOpacity>
                                    );
                                })}
                            </ScrollView>
                        </CutCard>
                    );
                        })}
                    </View>
                )}
            </ScrollView>
            <MeetingDetailSheet meeting={selectedMeeting} onClose={() => setSelectedMeeting(null)} />
        </>
    );
});

// ============ OVERALL STATS (isolated to prevent rerender on date change) ============
const OverallStats = React.memo(({ smallGroupTypeID, typeNamePlural }) => {
    const { data: reportingStats, isLoading } = Query.smallGroups.reportingStats({ smallGroupTypeID });
    const { data: activeGroupsDetail, isFetching: isLoadingActiveGroupsDetail, refetch: refetchActiveGroupsDetail } = Query.smallGroups.activeGroupsDetail({ smallGroupTypeID });
    const { data: activeLeadersDetail, isFetching: isLoadingActiveLeadersDetail, refetch: refetchActiveLeadersDetail } = Query.smallGroups.activeLeadersDetail({ smallGroupTypeID });
    const { data: activeStudentsDetail, isFetching: isLoadingActiveStudentsDetail, refetch: refetchActiveStudentsDetail } = Query.smallGroups.activeStudentsDetail({ smallGroupTypeID });

    return (
        <View style={{}}>
            <GroupLabel>Active {typeNamePlural}</GroupLabel>
            <View style={[styles.kpiGrid, { paddingTop: 8 }]}>
                <KPICard
                    title={`Active ${typeNamePlural}`}
                    content={isLoading ? <ActivityIndicator size={26} /> : (reportingStats?.active_groups_count ?? 0)}
                    onDetailsOpen={refetchActiveGroupsDetail}
                    detailsLoading={isLoadingActiveGroupsDetail}
                    detailsTitle="Breakdown"
                    details={activeGroupsDetail ? {
                        columns: [
                            { key: 'group_name', label: 'Group', width: 150, link: (row) => row.group_id ? `/smallgroups/${row.group_id}` : null },
                            { key: 'leader', label: 'Leader', width: 150, link: (row) => row.leader_id ? `/people/${row.leader_id}` : null },
                            { key: 'student_count', label: 'Members', width: 80 },
                        ],
                        data: activeGroupsDetail.map(r => ({
                            group_id: r.group_id,
                            group_name: r.group_name,
                            leader_id: r.leader_id,
                            leader: `${r.leader_first_name || ''} ${r.leader_last_name || ''}`.trim(),
                            student_count: r.student_count
                        }))
                    } : null}
                />
                <KPICard
                    title={`Active Leaders`}
                    content={isLoading ? <ActivityIndicator size={26} /> : (reportingStats?.active_teachers_count ?? 0)}
                    onDetailsOpen={refetchActiveLeadersDetail}
                    detailsLoading={isLoadingActiveLeadersDetail}
                    detailsTitle="Breakdown"
                    details={activeLeadersDetail ? {
                        columns: [
                            { key: 'name', label: 'Name', width: 200, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                            { key: 'group_count', label: 'Groups', width: 80 },
                        ],
                        data: activeLeadersDetail.map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            group_count: r.group_count
                        }))
                    } : null}
                />
                <KPICard
                    title={`Active Members`}
                    content={isLoading ? <ActivityIndicator size={26} /> : (reportingStats?.active_students_count ?? 0)}
                    onDetailsOpen={refetchActiveStudentsDetail}
                    detailsLoading={isLoadingActiveStudentsDetail}
                    detailsTitle="Breakdown"
                    details={activeStudentsDetail ? {
                        columns: [
                            { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                            { key: 'sessions_attended', label: 'Sessions', width: 80 },
                            { key: 'group_name', label: 'Group', width: 150 },
                        ],
                        data: activeStudentsDetail.map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            sessions_attended: r.sessions_attended,
                            group_name: r.group_name
                        }))
                    } : null}
                />
            </View>
        </View>
    );
});

// ============ BY DATE TAB (isolated to prevent remount on detail query state changes) ============
const ByDateTab = React.memo(({
    selectedType,
    typeName,
    typeNamePlural,
    startDate,
    endDate,
    isWeb,
    isNative,
    selectedPreset,
    handlePresetChange,
    handleStartDateChange,
    handleEndDateChange
}) => {
    const sessionsGroupedChartRef = useRef(null);
    const topTeachersBySessionsChartRef = useRef(null);
    const topTeachersByStudentsChartRef = useRef(null);

    const chartParameters = useMemo(() => ({
        smallGroupTypeID: selectedType?.id || '',
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : '',
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : ''
    }), [selectedType, startDate, endDate]);

    const refreshList = () => {
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'dateStats'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'teachersDetail'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'studentsDetail'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'sessionsDetail'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'newGroupsDetail'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'completedGroupsDetail'] });
        queryClient.invalidateQueries({ queryKey: ['smallGroups', 'reportingStats'] });
        sessionsGroupedChartRef.current?.reload();
        topTeachersBySessionsChartRef.current?.reload();
        topTeachersByStudentsChartRef.current?.reload();
    };

    const { data: dateStats, isLoading: isLoadingDateStats } = Query.smallGroups.dateStats({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    const { data: teachersDetail, isFetching: isLoadingTeachersDetail, refetch: refetchTeachersDetail } = Query.smallGroups.teachersDetail({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    const { data: studentsDetail, isFetching: isLoadingStudentsDetail, refetch: refetchStudentsDetail } = Query.smallGroups.studentsDetail({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    const { data: sessionsDetail, isFetching: isLoadingSessionsDetail, refetch: refetchSessionsDetail } = Query.smallGroups.sessionsDetail({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    const { data: newGroupsDetail, isFetching: isLoadingNewGroupsDetail, refetch: refetchNewGroupsDetail } = Query.smallGroups.newGroupsDetail({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    const { data: completedGroupsDetail, isFetching: isLoadingCompletedGroupsDetail, refetch: refetchCompletedGroupsDetail } = Query.smallGroups.completedGroupsDetail({
        smallGroupTypeID: selectedType?.id,
        startDate: startDate ? DateTime.fromJSDate(startDate).toISODate() : undefined,
        endDate: endDate ? DateTime.fromJSDate(endDate).toISODate() : undefined,
    });

    if (!selectedType) {
        return (
            <View style={styles.emptyContainer}>
                <Callout type="info" message={`Select a ${typeName} Type above to view reporting data.`} />
            </View>
        );
    }

    return (
        <ScrollView contentContainerStyle={styles.screen} keyboardShouldPersistTaps="handled" refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList} />}>
            {/* ===== SECTION 1: Overall Stats (date-independent) ===== */}
            <SectionHeader title="Overall Stats" subtitle="Cumulative metrics, independent of the selected range" />
            <OverallStats smallGroupTypeID={selectedType?.id} typeNamePlural={typeNamePlural} />

            {/* ===== SECTION 2: By Date Range ===== */}
            <SectionHeader title="By Date Range" subtitle="Metrics for the selected period" />

            <AnalyticsDateRangeControls
                presets={DATE_PRESETS}
                selectedPreset={selectedPreset}
                startDate={startDate}
                endDate={endDate}
                onPresetChange={handlePresetChange}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                isNative={isNative}
                testIDPrefix="overview-date-range"
            />

            <GroupLabel>Sessions & Participation</GroupLabel>
            <View style={styles.kpiGrid}>
                <KPICard
                    title="Total Sessions"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.total_sessions ?? 0)}
                    onDetailsOpen={refetchSessionsDetail}
                    detailsLoading={isLoadingSessionsDetail}
                    detailsTitle="Breakdown"
                    details={sessionsDetail ? {
                        columns: [
                            { key: 'session_date', label: 'Date', width: 120 },
                            { key: 'group_name', label: 'Group', width: 150 },
                            { key: 'teacher', label: 'Leader', width: 150 },
                            { key: 'attendance_count', label: 'Attendance', width: 90 },
                        ],
                        data: sessionsDetail.map(r => ({
                            session_date: r.session_date ? DateTime.fromISO(r.session_date).toFormat('MM/dd/yyyy') : '',
                            group_name: r.group_name,
                            teacher: `${r.teacher_first_name || ''} ${r.teacher_last_name || ''}`.trim(),
                            attendance_count: r.attendance_count
                        }))
                    } : null}
                />
                <KPICard
                    title="Unique Leaders"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.unique_teachers ?? 0)}
                    onDetailsOpen={refetchTeachersDetail}
                    detailsLoading={isLoadingTeachersDetail}
                    detailsTitle="Breakdown"
                    details={teachersDetail ? {
                        columns: [
                            { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                            { key: 'session_count', label: 'Sessions', width: 80 },
                            { key: 'group_name', label: 'Group', width: 150 },
                        ],
                        data: teachersDetail.map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            session_count: r.session_count,
                            group_name: r.group_name
                        }))
                    } : null}
                />
                <KPICard
                    title="Unique Members"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.unique_students ?? 0)}
                    onDetailsOpen={refetchStudentsDetail}
                    detailsLoading={isLoadingStudentsDetail}
                    detailsTitle="Breakdown"
                    details={studentsDetail ? {
                        columns: [
                            { key: 'name', label: 'Name', width: 150, link: (row) => row.person_id ? `/people/${row.person_id}` : null },
                            { key: 'sessions_attended', label: 'Sessions', width: 80 },
                            { key: 'group_name', label: 'Group', width: 150 },
                        ],
                        data: studentsDetail.map(r => ({
                            person_id: r.person_id,
                            name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                            first_name: r.first_name,
                            last_name: r.last_name,
                            email: r.email,
                            phones: r.phones,
                            sessions_attended: r.sessions_attended,
                            group_name: r.group_name
                        }))
                    } : null}
                />
                <KPICard
                    title="Sessions / Week"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.sessions_per_week ?? 0)}
                />
                <KPICard
                    title="Leaders / Week"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.teachers_per_week ?? 0)}
                />
                <KPICard
                    title="Members / Week"
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.students_per_week ?? 0)}
                />
            </View>

            <GroupLabel>Groups</GroupLabel>
            <View style={styles.kpiGrid}>
                <KPICard
                    title={`New ${typeNamePlural}`}
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.new_groups ?? 0)}
                    onDetailsOpen={refetchNewGroupsDetail}
                    detailsLoading={isLoadingNewGroupsDetail}
                    detailsTitle="Breakdown"
                    details={newGroupsDetail ? {
                        columns: [
                            { key: 'group_name', label: 'Group', width: 150, link: (row) => row.group_id ? `/smallgroups/${row.group_id}` : null },
                            { key: 'leader', label: 'Leader', width: 150, link: (row) => row.leader_id ? `/people/${row.leader_id}` : null },
                            { key: 'created_at', label: 'Created', width: 110 },
                        ],
                        data: newGroupsDetail.map(r => ({
                            group_id: r.group_id,
                            group_name: r.group_name,
                            leader_id: r.leader_id,
                            leader: `${r.leader_first_name || ''} ${r.leader_last_name || ''}`.trim(),
                            created_at: r.created_at ? DateTime.fromISO(r.created_at).toFormat('MM/dd/yyyy') : ''
                        }))
                    } : null}
                />
                <KPICard
                    title={`Completed ${typeNamePlural}`}
                    content={isLoadingDateStats ? <ActivityIndicator size={26} /> : (dateStats?.completed_groups ?? 0)}
                    onDetailsOpen={refetchCompletedGroupsDetail}
                    detailsLoading={isLoadingCompletedGroupsDetail}
                    detailsTitle="Breakdown"
                    details={completedGroupsDetail ? {
                        columns: [
                            { key: 'group_name', label: 'Group', width: 150, link: (row) => row.group_id ? `/smallgroups/${row.group_id}` : null },
                            { key: 'leader', label: 'Leader', width: 150, link: (row) => row.leader_id ? `/people/${row.leader_id}` : null },
                            { key: 'completed_at', label: 'Completed', width: 110 },
                        ],
                        data: completedGroupsDetail.map(r => ({
                            group_id: r.group_id,
                            group_name: r.group_name,
                            leader_id: r.leader_id,
                            leader: `${r.leader_first_name || ''} ${r.leader_last_name || ''}`.trim(),
                            completed_at: r.completed_at ? DateTime.fromISO(r.completed_at).toFormat('MM/dd/yyyy') : ''
                        }))
                    } : null}
                />
            </View>

            <GroupLabel>Charts</GroupLabel>
            <View style={styles.chartGrid}>
                <Chart ref={sessionsGroupedChartRef} height={isWeb ? 500 : 400} chartName="smallGroups/SessionsGroupedChart" parameters={chartParameters} />
                <Chart ref={topTeachersBySessionsChartRef} scrollEnabled height={isWeb ? 500 : 400} chartName="smallGroups/TopTeachersBySessionsChart" parameters={chartParameters} />
                <Chart ref={topTeachersByStudentsChartRef} scrollEnabled height={isWeb ? 500 : 400} chartName="smallGroups/TopTeachersByStudentsChart" parameters={chartParameters} />
            </View>
        </ScrollView>
    );
});

export default function SmallGroupReportingScreen() {
    const [selectedType, setSelectedType] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [shortcutModalOpen, setShortcutModalOpen] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState('last_30_days');
    
    const router = useRouter();
    const params = useLocalSearchParams();
    const navigation = useNavigation();
    const pathname = usePathname();

    const {data: loggedInUser} = Query.people.loggedInUser();
    const { data: smallGroupTypes } = Query.smallGroupTypes.all();
    const { data: organization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const orgTimezone = organization?.timezone;
    const { isWeb, isNative } = usePlatform();

    useEffect(() => {
        if (!smallGroupTypes || !params.typeID) return;
        const matchedType = smallGroupTypes.find(t => t.id === params.typeID);
        if (matchedType) {
            setSelectedType(matchedType);
        }
    }, [smallGroupTypes, params.typeID]);

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title={`${selectedType?.name} Reporting`} />
        });
    }, [navigation, selectedType])

    useEffect(() => {
        if (initialized || !orgTimezone) return;
        
        const { preset, startDate: urlStart, endDate: urlEnd } = params;
        
        if (preset && preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setSelectedPreset(preset);
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            } else {
                const defaultDates = getPresetDates('last_30_days', orgTimezone);
                setStartDate(defaultDates.start);
                setEndDate(defaultDates.end);
            }
        } else if (urlStart && urlEnd) {
            setSelectedPreset('custom');
            setStartDate(DateTime.fromISO(urlStart).startOf('day').toJSDate());
            setEndDate(DateTime.fromISO(urlEnd).startOf('day').toJSDate());
        } else {
            const defaultDates = getPresetDates('last_30_days', orgTimezone);
            setStartDate(defaultDates.start);
            setEndDate(defaultDates.end);
        }
        
        setInitialized(true);
    }, [params, initialized, orgTimezone]);

    useEffect(() => {
        if (!initialized || !startDate || !endDate) return;
        
        const newParams = {};
        if (selectedType) {
            newParams.typeID = selectedType.id;
        }
        if (selectedPreset !== 'custom') {
            newParams.preset = selectedPreset;
        } else {
            newParams.startDate = DateTime.fromJSDate(startDate).toISODate();
            newParams.endDate = DateTime.fromJSDate(endDate).toISODate();
        }
        
        router.setParams(newParams);
    }, [startDate, endDate, selectedPreset, initialized, selectedType]);

    const handlePresetChange = (preset) => {
        setSelectedPreset(preset);
        if (preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            }
        }
    };

    const handleStartDateChange = (date) => {
        setSelectedPreset('custom');
        setStartDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
    };

    const handleEndDateChange = (date) => {
        setSelectedPreset('custom');
        setEndDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
    };

    const currentUrl = useMemo(() => {
        if (!startDate || !endDate) return pathname;
        const searchParams = new URLSearchParams();
        if (selectedType) {
            searchParams.set('typeID', selectedType.id);
        }
        if (selectedPreset && selectedPreset !== 'custom') {
            searchParams.set('preset', selectedPreset);
        } else {
            searchParams.set('startDate', DateTime.fromJSDate(startDate).toISODate());
            searchParams.set('endDate', DateTime.fromJSDate(endDate).toISODate());
        }
        return `${pathname}?${searchParams.toString()}`;
    }, [pathname, selectedType, startDate, endDate, selectedPreset]);

    useEffect(() => {
        const menuOptions = [
            { name: 'Save as Shortcut', onSelect: () => setShortcutModalOpen(true) }
        ];

        navigation.setOptions({
            headerRight: () => <DropdownMenu options={menuOptions} />
        });
    }, [navigation, currentUrl]);

    const typeName = selectedType?.name || 'Group';
    const typeNamePlural = typeName[typeName.length-1] === 'y' ? typeName.slice(0, -1) + 'ies' : typeName + 's';

    const routes = useMemo(() => [
        { key: 'byDate', title: `Overview` },
        ...(loggedInUser?.isAdmin ? [
            { key: 'leaders', title: 'Leaders' },
        ] : []),
    ], [loggedInUser?.isAdmin]);

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'byDate':
                return <ByDateTab
                    selectedType={selectedType}
                    typeName={typeName}
                    typeNamePlural={typeNamePlural}
                    startDate={startDate}
                    endDate={endDate}
                    isWeb={isWeb}
                    isNative={isNative}
                    selectedPreset={selectedPreset}
                    handlePresetChange={handlePresetChange}
                    handleStartDateChange={handleStartDateChange}
                    handleEndDateChange={handleEndDateChange}
                />;
            case 'leaders':
                return loggedInUser?.can?.("Edit All Small Groups") ? <LeadersTab
                    selectedType={selectedType}
                    startDate={startDate}
                    endDate={endDate}
                    selectedPreset={selectedPreset}
                    handlePresetChange={handlePresetChange}
                    handleStartDateChange={handleStartDateChange}
                    handleEndDateChange={handleEndDateChange}
                    isNative={isNative}
                    isAdmin={loggedInUser?.isAdmin}
                /> : null;
            default:
                return null;
        }
    }, [isWeb, typeName, loggedInUser, selectedType, typeNamePlural, startDate, endDate, isNative, selectedPreset, handlePresetChange, handleStartDateChange, handleEndDateChange]);

    const renderTabBar = useCallback((props) => (
        <ReachTabBar {...props} scrollEnabled={true} getTestID={({ route }) => `smallgroups-tab-${route.key}`} />
    ), []);

    return (
        <View style={styles.container}>
            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="people-circle-outline" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>{typeName} Analytics</Text>
                <Text style={styles.heroDescription}>
                    View participation, leader effectiveness, and trends for {typeNamePlural}.
                </Text>
            </View>

            {/* <View style={styles.selectorContainer}>
                <SheetSelector
                    type="smallGroupTypes"
                    title="Group Type"
                    placeholder="Select a Group Type"
                    value={selectedType}
                    onChange={(selected) => setSelectedType(selected)}
                />
            </View> */}

            {selectedType ? (
                <TabView
                    navigationState={{ index: tabIndex, routes }}
                    renderScene={renderScene}
                    onIndexChange={setTabIndex}
                    renderTabBar={renderTabBar}
                    swipeEnabled={false}
                    lazy
                />
            ) : (
                <View style={styles.emptyContainer}>
                    <Callout type="info" message="Select a group type from the Reports page to view analytics." />
                </View>
            )}

            <SaveShortcutModal
                isOpen={shortcutModalOpen}
                onClose={() => setShortcutModalOpen(false)}
                personID={loggedInUser?.id}
                screen={currentUrl}
                icon="bar-chart-outline"
                library="ionicon"
                label={selectedPreset && selectedPreset !== 'custom'
                    ? `${DATE_PRESETS.find(d => d.value === selectedPreset)?.label} - ${selectedType?.name || typeName}`
                    : `${DateTime.fromJSDate(startDate).toFormat("MMM dd, yyyy")} to ${DateTime.fromJSDate(endDate).toFormat("MMM dd, yyyy")} - ${selectedType?.name || typeName}`}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }, { isPortrait }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    hero: {
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 20,
        gap: 10,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },
    selectorContainer: {
        paddingHorizontal: 15,
        paddingTop: 10,
        alignItems: 'flex-start',
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    emptyContainer: {
        padding: 20,
        justifyContent: 'center',
    },
    tabContent: {
        // flex: 1,
    },
    tabContentContainer: {
        paddingBottom: 100,
    },
    tableContainer: {
        flex: 1,
        minHeight: 300,
    },

    // Overview tab layout
    screen: {
        padding: 16,
        gap: 14,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },

    // Control panel
    controlPanel: {
        backgroundColor: colors.surface,
        borderRadius: 16,
        padding: 16,
        gap: 14,
        boxShadow: shadows.card,
    },
    controlPanelHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6,
    },
    controlPanelLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    controlPanelRange: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    presetsScrollNative: {
        flexGrow: 0,
        marginHorizontal: -16,
        paddingHorizontal: 16,
    },
    presetsScrollWeb: {
        flexGrow: 0,
    },
    presetsContainer: {
        flexDirection: 'row',
        gap: 8,
        paddingVertical: 2,
    },
    presetPill: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: colors.background,
        _web:{
            transition: 'background-color 0.2s ease-in-out',
            _hover:{
                backgroundColor: colors.hoverStrong,
            }
        }
    },
    presetPillActive: {
        backgroundColor: colors.primary,
        _web:{
            transition: 'background-color 0.2s ease-in-out',
            _hover:{
                backgroundColor: colors.primary,
            }
        }
    },
    presetPillText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    presetPillTextActive: {
        color: colors.onPrimary,
        fontFamily: fonts.heading,
    },
    filtersRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
    },
    filterItem: {
        flexGrow: isPortrait ? 1 : 0,
    },

    // Leader report
    refreshButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 18,
        backgroundColor: colors.primaryContainer,
    },
    refreshButtonText: {
        color: colors.primary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    // CutCard owns surface/radius; the shadow stays here so it converts
    // to a clip-path drop-shadow on web.
    reportState: {
        minHeight: 180,
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    reportStateContent: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: 24,
    },
    reportStateText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 14,
        textAlign: 'center',
    },
    reportErrorTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 17,
        textAlign: 'center',
    },
    retryButton: {
        marginTop: 4,
        paddingHorizontal: 18,
        paddingVertical: 9,
        borderRadius: 20,
        backgroundColor: colors.primary,
    },
    retryButtonText: {
        color: colors.onPrimary,
        fontFamily: fonts.heading,
        fontSize: 14,
    },
    leaderGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 12,
    },
    leaderCard: (isNarrow) => ({
        minWidth: isNarrow ? '100%' : 300,
        maxWidth: isNarrow ? '100%' : 460,
        flexBasis: isNarrow ? '100%' : 300,
        flexGrow: 1,
        // CutCard owns surface/radius; the shadow stays here so it converts
        // to a clip-path drop-shadow on web.
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    }),
    leaderCardContent: {
        padding: 12,
        gap: 10,
    },
    leaderCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 9,
    },
    leaderHeading: {
        flex: 1,
        gap: 1,
    },
    leaderCardName: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 16,
    },
    leaderMeetingCount: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    leaderAttendeeTotal: {
        alignItems: 'flex-end',
        minWidth: 58,
    },
    leaderAttendeeTotalValue: {
        color: colors.primary,
        fontFamily: fonts.bold,
        fontSize: 23,
        lineHeight: 25,
    },
    leaderAttendeeTotalLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 10,
    },
    leaderStatsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    leaderCompactStat: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexGrow: 1,
        gap: 3,
        backgroundColor: colors.background,
        borderRadius: 8,
        paddingHorizontal: 7,
        paddingVertical: 5,
    },
    leaderCompactStatValue: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 14,
    },
    leaderCompactStatLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 10,
    },
    leaderStat: {
        minWidth: isPortrait ? '30%' : 92,
        flexGrow: 1,
        backgroundColor: colors.background,
        borderRadius: 10,
        paddingHorizontal: 10,
        paddingVertical: 9,
    },
    leaderStatValue: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 17,
    },
    leaderStatLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 11,
        marginTop: 1,
    },
    meetingListScroll: {
        maxHeight: 220,
    },
    meetingList: {
        gap: 6,
    },
    meetingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: colors.background,
        borderRadius: 10,
        paddingHorizontal: 9,
        paddingVertical: 8,
        _web: {
            cursor: 'pointer',
            _hover: {
                backgroundColor: colors.hoverStrong,
            },
        },
    },
    meetingMain: {
        minWidth: 108,
        flexGrow: 1,
        gap: 1,
    },
    meetingDate: (didMeet) => ({
        color: didMeet ? colors.text : colors.warning,
        fontFamily: fonts.heading,
        fontSize: 13,
    }),
    meetingGroup: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 11,
    },
    meetingArrow: {
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 'auto',
        width: 18,
    },
    didNotMeetLabel: {
        color: colors.warning,
        fontFamily: fonts.heading,
        fontSize: 12,
    },
    meetingDetailScroll: {
        flex: 1,
    },
    meetingDetailContent: {
        padding: 16,
        paddingBottom: 40,
        gap: 18,
    },
    detailCountGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    detailSection: {
        gap: 10,
    },
    detailSectionTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 16,
    },
    detailText: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 14,
        lineHeight: 20,
    },
    detailEmptyText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 14,
        fontStyle: 'italic',
    },
    attendeeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 7,
    },
    attendeeInfo: {
        flex: 1,
        gap: 2,
    },
    attendeeName: {
        color: colors.text,
        fontFamily: fonts.heading,
        fontSize: 14,
    },
    attendeeClassification: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
    customFieldRow: {
        gap: 3,
        paddingVertical: 6,
    },
    customFieldLabel: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 12,
    },
    customFieldValue: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 14,
    },

    // Section headers
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 10,
    },
    sectionAccent: {
        width: 4,
        alignSelf: 'stretch',
        minHeight: 30,
        borderRadius: 4,
        backgroundColor: colors.primary,
    },
    sectionHeaderText: {
        gap: 2,
    },
    sectionTitle: {
        fontSize: 19,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sectionSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        paddingBottom: 8,
    },
    groupLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginTop: 4,
    },

    // Grids
    kpiGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    chartGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
}));
